/**
 * ExternalSystemsManagement - Manage external system integrations and API keys
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Server,
  Plus,
  Key,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Copy,
  ChevronDown,
  ChevronUp,
  Trash2,
  RotateCw,
  Eye,
  EyeOff,
  Globe,
  Shield,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import {
  externalSystemsAPI,
  ExternalSystem,
  ExternalSystemRegisterRequest,
  AccessLogEntry,
} from '@/services/api/externalSystems';
import { useFormattedDate } from '../../hooks/useUserTimezone';

interface ExternalSystemsManagementProps {
  organizationId: number;
  organizationName: string;
}

const SYSTEM_TYPE_OPTIONS = [
  { value: 'api', label: 'API' },
  { value: 'hie', label: 'HIE' },
  { value: 'qhio', label: 'QHIO' },
  { value: 'ehr', label: 'EHR' },
];

const AUTH_TYPE_OPTIONS = [
  { value: 'api_key', label: 'API Key' },
  { value: 'oauth2', label: 'OAuth 2.0' },
  { value: 'mtls', label: 'mTLS' },
];

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  suspended: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  revoked: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

export function ExternalSystemsManagement({
  organizationId,
  organizationName,
}: ExternalSystemsManagementProps) {
  const tabbed = useTabbedChrome();
  const { fmt } = useFormattedDate();
  const [systems, setSystems] = useState<ExternalSystem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [detailSystem, setDetailSystem] = useState<ExternalSystem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Registration form
  const [showRegister, setShowRegister] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [formData, setFormData] = useState<ExternalSystemRegisterRequest>({
    name: '',
    system_type: 'api',
    endpoint_url: '',
    description: '',
    allowed_programs: [],
    allowed_fields: [],
    rate_limit_per_minute: 60,
  });

  // API key display
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [keyVisible, setKeyVisible] = useState(false);

  // Confirm actions
  const [confirmRevoke, setConfirmRevoke] = useState<number | null>(null);
  const [confirmRotate, setConfirmRotate] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchSystems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await externalSystemsAPI.listSystems(organizationId);
      setSystems(result);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load external systems');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchSystems();
  }, [fetchSystems]);

  const handleRegister = async () => {
    if (!formData.name.trim()) {
      setError('System name is required');
      return;
    }
    setRegisterLoading(true);
    setError(null);
    try {
      const result = await externalSystemsAPI.registerSystem(organizationId, formData);
      setNewApiKey(result.api_key);
      setShowRegister(false);
      setFormData({
        name: '',
        system_type: 'api',
        endpoint_url: '',
        description: '',
        allowed_programs: [],
        allowed_fields: [],
        rate_limit_per_minute: 60,
      });
      fetchSystems();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to register system');
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleExpand = async (systemId: number) => {
    if (expandedId === systemId) {
      setExpandedId(null);
      setDetailSystem(null);
      return;
    }
    setExpandedId(systemId);
    setDetailLoading(true);
    try {
      const detail = await externalSystemsAPI.getSystem(organizationId, systemId);
      setDetailSystem(detail);
    } catch {
      setDetailSystem(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRevoke = async (systemId: number) => {
    setActionLoading(true);
    try {
      await externalSystemsAPI.revokeSystem(organizationId, systemId);
      setSuccess('System revoked');
      setConfirmRevoke(null);
      setExpandedId(null);
      fetchSystems();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to revoke system');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRotateKey = async (systemId: number) => {
    setActionLoading(true);
    try {
      const result = await externalSystemsAPI.rotateKey(organizationId, systemId);
      setNewApiKey(result.api_key);
      setConfirmRotate(null);
      fetchSystems();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to rotate key');
    } finally {
      setActionLoading(false);
    }
  };

  const copyKey = async () => {
    if (newApiKey) {
      await navigator.clipboard.writeText(newApiKey);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    }
  };

  const activeCount = systems.filter((s) => s.status === 'active').length;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {!tabbed && <Server className="w-7 h-7 text-blue-600 dark:text-blue-400" />}
          <div>
            {!tabbed && (
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              External Systems
            </h1>
            )}
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage API integrations for {organizationName}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchSystems}
            disabled={loading}
            className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 flex items-center gap-1.5"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowRegister(!showRegister)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" /> Register System
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
          <span className="text-sm text-green-700 dark:text-green-300">{success}</span>
        </div>
      )}

      {/* API Key Modal */}
      {newApiKey && (
        <Card className="border-amber-300 dark:border-amber-700">
          <CardContent className="py-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  API Key Generated
                </span>
              </div>
              <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono text-gray-800 dark:text-gray-200 break-all">
                    {keyVisible ? newApiKey : newApiKey.slice(0, 12) + '•'.repeat(28)}
                  </code>
                  <button
                    onClick={() => setKeyVisible(!keyVisible)}
                    className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    title={keyVisible ? 'Hide' : 'Show'}
                  >
                    {keyVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={copyKey}
                    className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    title="Copy"
                  >
                    {keyCopied ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400">
                <AlertTriangle className="w-3.5 h-3.5" />
                This key will not be shown again. Copy it now and store securely.
              </div>
              <button
                onClick={() => {
                  setNewApiKey(null);
                  setKeyVisible(false);
                  setKeyCopied(false);
                }}
                className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                Dismiss
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Registration Form */}
      {showRegister && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Register New External System</CardTitle>
            <CardDescription>
              Configure a new external system integration with API key authentication
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  System Name *
                </label>
                <input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. County HIE Gateway"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  System Type
                </label>
                <select
                  value={formData.system_type}
                  onChange={(e) => setFormData({ ...formData, system_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                >
                  {SYSTEM_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Endpoint URL
                </label>
                <input
                  value={formData.endpoint_url}
                  onChange={(e) => setFormData({ ...formData, endpoint_url: e.target.value })}
                  placeholder="https://api.example.com"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Rate Limit (req/min)
                </label>
                <input
                  type="number"
                  value={formData.rate_limit_per_minute}
                  onChange={(e) =>
                    setFormData({ ...formData, rate_limit_per_minute: parseInt(e.target.value) || 60 })
                  }
                  min={1}
                  max={1000}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this system integration..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm resize-none"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={handleRegister}
                disabled={registerLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                {registerLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Key className="w-4 h-4" />
                )}
                Register & Generate Key
              </button>
              <button
                onClick={() => setShowRegister(false)}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                Cancel
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {systems.length} system{systems.length !== 1 ? 's' : ''} registered
        </span>
        {activeCount > 0 && (
          <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">
            {activeCount} active
          </span>
        )}
      </div>

      {/* Systems List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500 mr-2" />
          <span className="text-gray-500">Loading systems...</span>
        </div>
      ) : systems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Server className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No external systems registered</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Register your first system to enable controlled data sharing
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {systems.map((system) => (
            <Card key={system.id}>
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    {system.system_type === 'hie' || system.system_type === 'qhio' ? (
                      <Globe className="w-5 h-5 text-blue-500" />
                    ) : system.system_type === 'ehr' ? (
                      <Shield className="w-5 h-5 text-purple-500" />
                    ) : (
                      <Server className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {system.name}
                      </h3>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          STATUS_COLORS[system.status] || 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {system.status}
                      </span>
                      <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 px-1.5 py-0.5 rounded font-mono uppercase">
                        {system.system_type}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {system.api_key_prefix && (
                        <span className="font-mono">
                          <Key className="w-3 h-3 inline mr-0.5" />
                          {system.api_key_prefix}...
                        </span>
                      )}
                      {system.endpoint_url && (
                        <span className="truncate max-w-xs">{system.endpoint_url}</span>
                      )}
                      {system.created_at && (
                        <span>Created {fmt.short(system.created_at)}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleExpand(system.id)}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                  >
                    Details
                    {expandedId === system.id ? (
                      <ChevronUp className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    )}
                  </button>
                </div>

                {/* Expanded Detail */}
                {expandedId === system.id && (
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-4">
                    {detailLoading ? (
                      <div className="flex items-center gap-2 py-4">
                        <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                        <span className="text-sm text-gray-500">Loading details...</span>
                      </div>
                    ) : (
                      <>
                        {/* System Info */}
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">
                              Description
                            </span>
                            <p className="text-gray-700 dark:text-gray-300">
                              {system.description || 'None'}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">
                              Auth Type
                            </span>
                            <p className="text-gray-700 dark:text-gray-300">
                              {AUTH_TYPE_OPTIONS.find((a) => a.value === system.auth_type)?.label ||
                                system.auth_type}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">
                              Rate Limit
                            </span>
                            <p className="text-gray-700 dark:text-gray-300">
                              {system.rate_limit_per_minute} req/min
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">
                              Programs
                            </span>
                            <p className="text-gray-700 dark:text-gray-300">
                              {system.allowed_programs?.length
                                ? `${system.allowed_programs.length} allowed`
                                : 'All'}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">Fields</span>
                            <p className="text-gray-700 dark:text-gray-300">
                              {system.allowed_fields?.length
                                ? `${system.allowed_fields.length} allowed`
                                : 'All'}
                            </p>
                          </div>
                          {system.revoked_at && (
                            <div>
                              <span className="text-gray-500 dark:text-gray-400 text-xs">
                                Revoked At
                              </span>
                              <p className="text-red-600 dark:text-red-400">
                                {fmt.dateTime(system.revoked_at)}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Recent Access Log */}
                        {detailSystem?.recent_access && detailSystem.recent_access.length > 0 && (
                          <div>
                            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                              Recent Access
                            </h4>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                    <th className="py-1.5 pr-3">Time</th>
                                    <th className="py-1.5 pr-3">Method</th>
                                    <th className="py-1.5 pr-3">Endpoint</th>
                                    <th className="py-1.5 pr-3">Status</th>
                                    <th className="py-1.5 pr-3">Latency</th>
                                    <th className="py-1.5">Records</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                                  {detailSystem.recent_access.map((log: AccessLogEntry) => (
                                    <tr key={log.id} className="text-gray-700 dark:text-gray-300">
                                      <td className="py-1.5 pr-3 text-gray-400">
                                        {log.accessed_at
                                          ? fmt.custom(log.accessed_at, 'MMM d, h:mm a')
                                          : '-'}
                                      </td>
                                      <td className="py-1.5 pr-3 font-mono">
                                        {log.method || '-'}
                                      </td>
                                      <td className="py-1.5 pr-3 truncate max-w-[200px]">
                                        {log.endpoint || '-'}
                                      </td>
                                      <td className="py-1.5 pr-3">
                                        <span
                                          className={
                                            log.status_code && log.status_code < 400
                                              ? 'text-green-600'
                                              : 'text-red-600'
                                          }
                                        >
                                          {log.status_code || '-'}
                                        </span>
                                      </td>
                                      <td className="py-1.5 pr-3">
                                        {log.response_time_ms != null
                                          ? `${log.response_time_ms}ms`
                                          : '-'}
                                      </td>
                                      <td className="py-1.5">{log.records_accessed}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        {system.status === 'active' && (
                          <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                            {confirmRotate === system.id ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-amber-700 dark:text-amber-400">
                                  Rotate key? Old key will stop working.
                                </span>
                                <button
                                  onClick={() => handleRotateKey(system.id)}
                                  disabled={actionLoading}
                                  className="px-2.5 py-1 text-xs font-medium text-amber-700 bg-amber-100 dark:bg-amber-900/20 rounded hover:bg-amber-200 disabled:opacity-50"
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={() => setConfirmRotate(null)}
                                  className="px-2.5 py-1 text-xs text-gray-500 hover:text-gray-700"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : confirmRevoke === system.id ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-red-700 dark:text-red-400">
                                  Revoke this system? This cannot be undone.
                                </span>
                                <button
                                  onClick={() => handleRevoke(system.id)}
                                  disabled={actionLoading}
                                  className="px-2.5 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
                                >
                                  Confirm Revoke
                                </button>
                                <button
                                  onClick={() => setConfirmRevoke(null)}
                                  className="px-2.5 py-1 text-xs text-gray-500 hover:text-gray-700"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <>
                                <button
                                  onClick={() => setConfirmRotate(system.id)}
                                  className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded-lg hover:bg-amber-100 flex items-center gap-1"
                                >
                                  <RotateCw className="w-3.5 h-3.5" /> Rotate Key
                                </button>
                                <button
                                  onClick={() => setConfirmRevoke(system.id)}
                                  className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 flex items-center gap-1"
                                >
                                  <Trash2 className="w-3.5 h-3.5" /> Revoke
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
